import { availableResidentSortTypes, normalizeResidentSortType } from '@/utils/residentSort';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { css } from '@emotion/react';
import { styled, Checkbox, Menu, MenuItem, Button, ListItemIcon, MenuProps, ListItemText } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckIcon from '@mui/icons-material/Check';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { color } from '../styles';
import { selfDataSelector, showMedicalContentSelector } from '../redux/config/selfData';
import { residentListSettingsSlice } from '../redux/config/residentListSettings';
import { saveResidentCategories } from '@/utils/residentCategories';
import { SortType } from '../types/components';

interface Props {
  title: string;
  categories: string[];
  sortType?: SortType;
  rightOfUpdateElement?: React.ReactNode;
  rightElement?: React.ReactNode;
  onUpdate: () => void;
}

const Container = css`
  position: sticky;
  z-index: 2;
  top: 0;
  padding: 16px 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  row-gap: 8px;
  background-color: var(--surface-list-header);
`;

const TitleContainer = css`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const Title = css`
  font-size: 20px;
  font-weight: bold;
  margin-right: 16px;
`;

const StyledMenu = styled((props: MenuProps) => <Menu {...props} />)(() => ({
  '& .MuiPaper-root': {
    '& .MuiMenuItem-root': {
      justifyContent: 'flex-end',
    },
  },
}));

const UpdateButton = css`
  border-radius: 100px;
  color: ${color.dark_gray};
  background-color: var(--button-bg);
  &:hover {
    background-color: var(--button-bg);
  }
`;

const RightElementContainer = css`
  display: flex;
  flex-shrink: 0;
  margin-left: auto;
`;

const SortButton = css`
  display: flex;
  align-items: center;
  column-gap: 4px;
`;

const LabelText = css`
  color: ${color.dark_gray};
`;

export const ResidentListHeader = React.memo(
  ({ title, sortType, categories, rightOfUpdateElement, rightElement, onUpdate }: Props) => {
    const { selfUser, selfUnits } = useSelector(selfDataSelector);
    const showMedicalContent = useSelector(showMedicalContentSelector);
    const dispatch = useDispatch();
    const [anchorElFilter, setAnchorElFilter] = React.useState<null | HTMLElement>(null);
    const [anchorElSort, setAnchorElSort] = React.useState<null | HTMLElement>(null);

    const isFilterMenuOpen = Boolean(anchorElFilter);
    const isSortMenuOpen = Boolean(anchorElSort);
    const { t } = useTranslation();
    const effectiveSort = normalizeResidentSortType(sortType, showMedicalContent);
    React.useEffect(() => {
      if (!sortType || sortType === effectiveSort) return;
      dispatch(residentListSettingsSlice.actions.setSortType(effectiveSort));
      localStorage.setItem('sort', effectiveSort);
    }, [sortType, effectiveSort, dispatch]);
    const sortLabels: Record<SortType, string> = {
      unit: t('ByUnit', 'ユニット順'),
      notification: t('ByNotificationElapsedTime', '通知経過時間順'),
      careTiming: t('ByToiletingCaretimePriority', '排泄ケア時刻優先度順'),
      notificationAndCareTiming: t('ByNotificationElapsedTimeAndToiletingCaretimePriority', '通知経過時間+排泄ケア時刻優先度順'),
      createdAt: t('ByCreationOrder', '作成順'),
      name: t('@byName', '名前順'),
      bed: t('@byBed', 'ベッド順'),
      daysWithoutDefecation: t('@byDaysWithoutBowelMovements', '最終排便がない日数順'),
      dfreeLatestUsage: t('@byMostRecentDFreeUse', 'DFree最終使用順'),
    };

    const handleFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorElFilter(event.currentTarget);
    };

    const handleSortClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorElSort(event.currentTarget);
    };

    const handleCloseFilter = () => {
      setAnchorElFilter(null);
    };

    const handleCloseSort = () => {
      setAnchorElSort(null);
    };

    const visibleCategories = selfUser.hasUnitPerm
      ? categories.filter((value) => !value.startsWith('unit-'))
      : categories;
    const categoryOptions = [
      { value: 'deviceAssigned', label: t('DFreeIsAssigned', 'デバイス登録中') },
      { value: 'deviceAttached', label: t('DFreeIsAttached', '測定中') },
      ...(!selfUser.hasUnitPerm
        ? selfUnits.map((unit) => ({ value: `unit-${unit.id}`, label: unit.name }))
        : []),
    ];

    const handleChangeSortType = (nextSortType: SortType) => {
      dispatch(residentListSettingsSlice.actions.setSortType(nextSortType));
      localStorage.setItem('sort', nextSortType);
      handleCloseSort();
    };

    const handleToggleCategory = (value: string) => {
      const nextCategories = visibleCategories.includes(value)
        ? visibleCategories.filter((category) => category !== value)
        : [...visibleCategories, value];
      dispatch(residentListSettingsSlice.actions.setFilterCondition({ categories: nextCategories }));
      saveResidentCategories(nextCategories);
    };

    return (
      <div css={Container}>
        <div css={TitleContainer}>
          <div css={Title}>{title}</div>
          <Button css={UpdateButton} onClick={onUpdate} startIcon={<RefreshIcon color='primary' />}>
            {t('update', '更新')}
          </Button>
          {rightOfUpdateElement && rightOfUpdateElement}
        </div>
        <div css={RightElementContainer}>
          {rightElement && rightElement}
          {sortType && (
            <>
              <Button onClick={handleSortClick} css={SortButton}>
                <span css={LabelText}>{t('Sort', '並び替え')}</span>
                <img src='/images/icons/sort.svg' width='18' height='18' alt={t('Sort', '並び替え')} />
              </Button>
              <StyledMenu open={isSortMenuOpen} anchorEl={anchorElSort} onClose={handleCloseSort}
                PaperProps={{ sx: { maxHeight: 'min(480px, 70vh)', maxWidth: 'calc(100vw - 32px)', overflowY: 'auto' } }}
                MenuListProps={{ sx: { py: 1 } }}>
                {availableResidentSortTypes(showMedicalContent).map((type) => (
                  <MenuItem key={type} selected={effectiveSort === type} onClick={() => handleChangeSortType(type)}
                    sx={{ py: 0.5, minHeight: 36, whiteSpace: 'normal' }}>
                    <ListItemIcon>{effectiveSort === type && <CheckIcon />}</ListItemIcon>
                    <ListItemText>{sortLabels[type]}</ListItemText>
                  </MenuItem>
                ))}
              </StyledMenu>
            </>
          )}

          <Button onClick={handleFilterClick}>
            <span css={LabelText}>{t('filter', '絞り込み')}</span>
            <>{visibleCategories.length === 0 ? <ArrowDropDownIcon /> : <FilterAltIcon />}</>
          </Button>
          <Menu
            open={isFilterMenuOpen}
            anchorEl={anchorElFilter}
            onClose={handleCloseFilter}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { minWidth: 280, maxWidth: 'calc(100vw - 32px)', maxHeight: 'min(480px, 70vh)', overflowY: 'auto', borderRadius: 2 } }}
            MenuListProps={{ sx: { py: 1 } }}
          >
            {categoryOptions.map((option) => (
              <MenuItem
                key={option.value}
                role='menuitemcheckbox'
                aria-checked={visibleCategories.includes(option.value)}
                onClick={() => handleToggleCategory(option.value)}
                sx={{ px: 2, py: 0.25, minHeight: 36, whiteSpace: 'normal' }}
              >
                <Checkbox
                  checked={visibleCategories.includes(option.value)}
                  tabIndex={-1}
                  disableRipple
                  inputProps={{ 'aria-hidden': true }}
                  size='small'
                  sx={{ p: 0.5, mr: 1, pointerEvents: 'none' }}
                />
                <ListItemText>{option.label}</ListItemText>
              </MenuItem>
            ))}
          </Menu>
        </div>
      </div>
    );
  }
);
